import { AutoplayType } from "@/utils/constants";

export type VideoAutoplayAction = "play" | "pause" | "none";

export const resolveVideoAutoplayPreference = (
  autoplay: AutoplayType,
  videoAutoplayEnabled: boolean,
) => {
  return videoAutoplayEnabled ? autoplay : AutoplayType.NEVER;
};

type VideoAutoplayActionOptions = {
  autoplay: AutoplayType;
  loaded: boolean;
  isIntersecting: boolean;
  hasActivePlayingElement: boolean;
  isFullscreen: boolean;
};

export const getVideoAutoplayAction = ({
  autoplay,
  loaded,
  isIntersecting,
  hasActivePlayingElement,
  isFullscreen,
}: VideoAutoplayActionOptions): VideoAutoplayAction => {
  if (!loaded || autoplay === AutoplayType.NEVER) {
    return "none";
  }

  const shouldPlay =
    autoplay === AutoplayType.ALWAYS ||
    (autoplay === AutoplayType.ON_INTERSECTION && isIntersecting);

  if (shouldPlay && !hasActivePlayingElement) {
    return "play";
  }

  if (
    autoplay === AutoplayType.ON_INTERSECTION &&
    !isIntersecting &&
    !isFullscreen
  ) {
    return "pause";
  }

  return "none";
};

export const isInterruptedPlayRequest = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === "AbortError" &&
    error.message.includes("play() request was interrupted")
  );
};

type VideoAutoplayRegistration = {
  element: HTMLElement | null;
  autoplay: AutoplayType;
  loaded: boolean;
  isIntersecting: boolean;
  play: () => void;
  pause: () => void;
  isPaused: () => boolean;
  isFullscreen: () => boolean;
  getActiveElement: () => HTMLMediaElement | null;
  getMediaElement: () => HTMLMediaElement | null;
};

type VideoAutoplayCandidate = {
  id: string;
  element: HTMLElement;
  autoplay: AutoplayType;
  loaded: boolean;
  isIntersecting: boolean;
};

const videoAutoplayRegistrations = new Map<string, VideoAutoplayRegistration>();
let activeAutoplayPlayerId: string | null = null;
let evaluateFrame: number | null = null;

export const getViewportCenterDistance = (
  rect: Pick<DOMRectReadOnly, "top" | "bottom">,
  viewportHeight: number,
) => {
  const viewportCenter = viewportHeight / 2;

  if (rect.top <= viewportCenter && rect.bottom >= viewportCenter) {
    return 0;
  }

  return Math.min(
    Math.abs(rect.top - viewportCenter),
    Math.abs(rect.bottom - viewportCenter),
  );
};

export const selectMiddleVideoAutoplayCandidate = (
  candidates: VideoAutoplayCandidate[],
  viewportHeight: number,
) => {
  return (
    candidates
      .filter(({ autoplay, loaded, isIntersecting }) => {
        if (!loaded || autoplay === AutoplayType.NEVER) {
          return false;
        }

        return autoplay === AutoplayType.ALWAYS || isIntersecting;
      })
      .map((candidate, index) => ({
        candidate,
        index,
        score:
          candidate.autoplay === AutoplayType.ALWAYS
            ? -1
            : getViewportCenterDistance(
                candidate.element.getBoundingClientRect(),
                viewportHeight,
              ),
      }))
      .sort((left, right) => {
        if (left.score !== right.score) {
          return left.score - right.score;
        }

        return left.index - right.index;
      })[0]?.candidate ?? null
  );
};

const hasWindow = () => typeof window !== "undefined";

const isManagedMediaElement = (element: HTMLMediaElement) => {
  for (const registration of videoAutoplayRegistrations.values()) {
    if (registration.getMediaElement() === element) {
      return true;
    }
  }

  return false;
};

const pauseActiveAutoplayPlayer = () => {
  if (!activeAutoplayPlayerId) {
    return;
  }

  const activeRegistration = videoAutoplayRegistrations.get(
    activeAutoplayPlayerId,
  );

  if (activeRegistration && !activeRegistration.isPaused()) {
    activeRegistration.pause();
  }

  activeAutoplayPlayerId = null;
};

const evaluateVideoAutoplayPlayers = () => {
  evaluateFrame = null;

  if (!hasWindow() || document.fullscreenElement) {
    return;
  }

  const selectedCandidate = selectMiddleVideoAutoplayCandidate(
    Array.from(videoAutoplayRegistrations.entries())
      .map(([id, registration]) => ({
        id,
        element: registration.element,
        autoplay: registration.autoplay,
        loaded: registration.loaded,
        isIntersecting: registration.isIntersecting,
      }))
      .filter(
        (candidate): candidate is VideoAutoplayCandidate =>
          candidate.element !== null && candidate.element.isConnected,
      ),
    window.innerHeight,
  );

  if (!selectedCandidate) {
    pauseActiveAutoplayPlayer();
    return;
  }

  const selectedRegistration = videoAutoplayRegistrations.get(
    selectedCandidate.id,
  );

  if (!selectedRegistration) {
    return;
  }

  const activeElement = selectedRegistration.getActiveElement();
  const hasExternalActiveMedia =
    activeElement &&
    !activeElement.paused &&
    !isManagedMediaElement(activeElement);

  if (hasExternalActiveMedia) {
    pauseActiveAutoplayPlayer();
    return;
  }

  if (
    activeAutoplayPlayerId &&
    activeAutoplayPlayerId !== selectedCandidate.id
  ) {
    pauseActiveAutoplayPlayer();
  }

  activeAutoplayPlayerId = selectedCandidate.id;

  if (selectedRegistration.isPaused()) {
    selectedRegistration.play();
  }
};

export const scheduleVideoAutoplayEvaluation = () => {
  if (!hasWindow() || evaluateFrame !== null) {
    return;
  }

  evaluateFrame = window.requestAnimationFrame(evaluateVideoAutoplayPlayers);
};

const addVideoAutoplayListeners = () => {
  if (!hasWindow()) {
    return;
  }

  window.addEventListener("scroll", scheduleVideoAutoplayEvaluation, {
    passive: true,
  });
  window.addEventListener("resize", scheduleVideoAutoplayEvaluation);
  document.addEventListener(
    "fullscreenchange",
    scheduleVideoAutoplayEvaluation,
  );
};

const removeVideoAutoplayListeners = () => {
  if (!hasWindow()) {
    return;
  }

  window.removeEventListener("scroll", scheduleVideoAutoplayEvaluation);
  window.removeEventListener("resize", scheduleVideoAutoplayEvaluation);
  document.removeEventListener(
    "fullscreenchange",
    scheduleVideoAutoplayEvaluation,
  );
};

export const updateVideoAutoplayPlayer = (
  id: string,
  registration: VideoAutoplayRegistration,
) => {
  const shouldAddListeners = videoAutoplayRegistrations.size === 0;

  videoAutoplayRegistrations.set(id, registration);

  if (shouldAddListeners) {
    addVideoAutoplayListeners();
  }

  scheduleVideoAutoplayEvaluation();
};

export const unregisterVideoAutoplayPlayer = (id: string) => {
  const registration = videoAutoplayRegistrations.get(id);

  if (activeAutoplayPlayerId === id) {
    activeAutoplayPlayerId = null;

    if (
      registration &&
      !registration.isFullscreen() &&
      !registration.isPaused()
    ) {
      registration.pause();
    }
  }

  videoAutoplayRegistrations.delete(id);

  if (videoAutoplayRegistrations.size === 0) {
    removeVideoAutoplayListeners();

    if (hasWindow() && evaluateFrame !== null) {
      window.cancelAnimationFrame(evaluateFrame);
      evaluateFrame = null;
    }
  } else {
    scheduleVideoAutoplayEvaluation();
  }
};
